<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ConversationApi from '../../../../api/inbox/conversation';
import AccountScheduledMessagesAPI from '../../../../api/accountScheduledMessages';
import FunnelAPI from '../../../../api/funnel';
import whatsappTemplatesAPI from '../../../../api/whatsappTemplates';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import ContextMenu from '../../../ui/ContextMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import WhatsAppTemplatePreview from 'dashboard/components/widgets/whatsapp/WhatsAppTemplatePreview.vue';

export default {
  components: {
    ContextMenu,
    Button,
    WootButton,
    WhatsAppTemplatePreview,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
    editingMessage: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      title: '',
      message: '',
      selectedInbox: '',
      scheduledTime: '',
      isLoading: false,
      scheduledMessages: [],
      isScheduledMessagesOpen: true,
      isEditing: false,
      editingMessageId: null,
      enabledFeatures: {},
      showDeleteConfirmation: false,
      messageToDelete: null,
      expandedMessages: [], // Array para controlar quais mensagens estão expandidas
      statusFilter: 'all', // Novo estado para o filtro
      showFilterMenu: false,
      filterMenuPosition: { x: 0, y: 0 },
      isRecurrent: false,
      selectedPeriod: '',
      // Novos estados para templates do Kanban/Funnel
      templates: [],
      selectedTemplate: null,
      loadingTemplates: false,
      // Templates WhatsApp (API oficial)
      whatsappTemplates: [],
      selectedWhatsappTemplate: null,
      loadingWhatsappTemplates: false,
      whatsappTemplateParams: {},
      isWhatsappInbox: false,
      // Busca de conversas quando abre sem conversationId
      selectedConversationId: null,
      conversationsForSelect: [],
      conversationSearch: '',
      conversationDropdownOpen: false,
      conversationDropdownStyle: { top: '0px', left: '0px', width: '100%' },
      loadingConversations: false,
      // Arquivos para anexar à mensagem agendada
      attachedFiles: [],
      existingFiles: [],
      removeFileIds: [],
    };
  },

  computed: {
    effectiveConversationId() {
      return this.conversationId || this.selectedConversationId;
    },

    filteredConversationsForSelect() {
      if (!this.conversationSearch.trim()) return this.conversationsForSelect;
      const q = this.conversationSearch.trim().toLowerCase();
      return this.conversationsForSelect.filter(c => {
        const sender = c.meta?.sender;
        const text =
          `${c.display_id || c.id} ${sender?.name || ''} ${sender?.email || ''}`.toLowerCase();
        return text.includes(q);
      });
    },

    selectedConversationForSchedule() {
      const id = this.effectiveConversationId;
      return id
        ? this.conversationsForSelect.find(c => (c.display_id || c.id) === id)
        : null;
    },

    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxes: 'inboxes/getInboxes',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      selectedFunnel: 'funnel/getSelectedFunnel',
    }),

    account() {
      return this.$store.getters['accounts/getAccount'](this.accountId);
    },

    isFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.SCHEDULE_MESSAGES
      );
    },

    filteredMessages() {
      if (this.statusFilter === 'all') {
        return this.scheduledMessages;
      }
      return this.scheduledMessages.filter(message => {
        const st = message.status || 'pending';
        return st === this.statusFilter;
      });
    },

    selectedInboxObject() {
      if (!this.selectedInbox) return null;
      return this.inboxes.find(i => i.id === this.selectedInbox);
    },

    isSelectedInboxWhatsapp() {
      const inbox = this.selectedInboxObject;
      return inbox && inbox.channel_type === INBOX_TYPES.WHATSAPP;
    },

    approvedWhatsappTemplates() {
      return this.whatsappTemplates.filter(
        t => t.status === 'APPROVED' || t.status === 'approved'
      );
    },

    filterMenuItems() {
      return [
        {
          id: 'all',
          label: this.$t('SCHEDULE_MESSAGES.FILTER.ALL'),
          icon: 'list',
        },
        {
          id: 'pending',
          label: this.$t('SCHEDULE_MESSAGES.FILTER.PENDING'),
          icon: 'clock',
        },
        {
          id: 'sent',
          label: this.$t('SCHEDULE_MESSAGES.FILTER.SENT'),
          icon: 'checkmark',
        },
      ];
    },

    currentContact() {
      const convId =
        this.effectiveConversationId || this.selectedConversationId;
      if (!convId) return {};
      const conversation = this.$store.getters.getConversationById(convId);
      if (!conversation?.meta?.sender) return {};
      return (
        this.$store.getters['contacts/getContact'](
          conversation.meta.sender.id
        ) ||
        conversation.meta.sender ||
        {}
      );
    },

    isDarkMode() {
      return document.documentElement.getAttribute('data-theme') === 'dark';
    },
  },

  watch: {
    editingMessage(newMessage) {
      if (newMessage && this.show) {
        this.handleEdit(newMessage);
      }
    },
    show(isVisible) {
      if (isVisible && this.editingMessage) {
        this.handleEdit(this.editingMessage);
      }
      if (isVisible) {
        if (!this.conversationId) {
          this.fetchConversationsForSelect();
        }
        this.fetchTemplates();
        if (this.effectiveConversationId) {
          this.fetchScheduledMessages();
        }
      }
      if (!isVisible) {
        this.resetForm();
        this.selectedConversationId = null;
      }
    },
    conversationId() {
      if (this.show && this.effectiveConversationId) {
        this.fetchScheduledMessages();
      }
    },
    selectedInbox(newInboxId, oldInboxId) {
      // Reset form fields when the channel changes to avoid sending stale data
      if (
        !this.isEditing &&
        oldInboxId !== undefined &&
        newInboxId !== oldInboxId
      ) {
        this.message = '';
        this.selectedTemplate = '';
        this.selectedWhatsappTemplate = null;
        this.whatsappTemplateParams = {};
        this.isRecurrent = false;
        this.selectedPeriod = '';
        this.attachedFiles = [];
      }

      if (newInboxId && this.isSelectedInboxWhatsapp) {
        this.fetchWhatsappTemplates(newInboxId);
      } else {
        this.whatsappTemplates = [];
        this.selectedWhatsappTemplate = null;
        this.whatsappTemplateParams = {};
      }
    },
  },

  async mounted() {
    document.addEventListener('mousedown', this.closeConversationDropdown);

    if (this.effectiveConversationId) await this.fetchScheduledMessages();
    await this.fetchTemplates();
    if (!this.isFeatureEnabled) {
      this.onClose();
    }

    if (this.editingMessage) {
      this.handleEdit(this.editingMessage);
    }
  },

  beforeUnmount() {
    document.removeEventListener('mousedown', this.closeConversationDropdown);
  },

  methods: {
    // Nova função para buscar templates
    async fetchTemplates() {
      try {
        this.loadingTemplates = true;
        const response = await FunnelAPI.get();
        const allTemplates = [];

        response.data.forEach(funnel => {
          Object.values(funnel.stages).forEach(stage => {
            if (stage.message_templates) {
              allTemplates.push(...stage.message_templates);
            }
          });
        });

        this.templates = allTemplates;
      } catch (error) {
        console.error(
          '[ScheduleMessageModal] Erro ao carregar templates:',
          error
        );
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.LOAD_TEMPLATES'), 'error');
      } finally {
        this.loadingTemplates = false;
      }
    },

    // Função para aplicar template selecionado (Kanban/Funnel)
    applyTemplate() {
      if (this.selectedTemplate) {
        this.message = this.selectedTemplate.content;
        this.title =
          this.selectedTemplate.title ||
          this.selectedTemplate.content.substring(0, 50) + '...';
      }
    },

    // WhatsApp Templates - Buscar templates aprovados da API oficial
    async fetchWhatsappTemplates(inboxId) {
      try {
        this.loadingWhatsappTemplates = true;
        const response = await whatsappTemplatesAPI.list(
          this.accountId,
          inboxId,
          { status: 'APPROVED' }
        );
        this.whatsappTemplates = response.data.templates || [];
      } catch (error) {
        console.error('[WhatsApp] Erro ao carregar templates:', error);
        this.whatsappTemplates = [];
      } finally {
        this.loadingWhatsappTemplates = false;
      }
    },

    // Aplica template WhatsApp selecionado
    applyWhatsappTemplate() {
      if (!this.selectedWhatsappTemplate) return;
      const template = this.selectedWhatsappTemplate;

      // Extrai o body do template
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      const headerComponent = template.components?.find(
        c => c.type === 'HEADER'
      );
      const footerComponent = template.components?.find(
        c => c.type === 'FOOTER'
      );

      let bodyText = bodyComponent?.text || '';
      let headerText = headerComponent?.text || '';

      // Preenche o campo mensagem com preview do template
      let preview = '';
      if (headerText) preview += `[Header] ${headerText}\n`;
      preview += bodyText;
      if (footerComponent?.text)
        preview += `\n[Footer] ${footerComponent.text}`;

      this.message = preview;
      this.title = this.title || template.name;

      // Detecta variáveis {{1}}, {{2}} etc no body
      const vars = bodyText.match(/\{\{\d+\}\}/g) || [];
      const params = {};
      vars.forEach(v => {
        const num = v.replace(/[{}]/g, '');
        params[num] = this.whatsappTemplateParams[num] || '';
      });
      this.whatsappTemplateParams = params;

      // Auto-fill das variáveis com dados do contato
      const contact = this.currentContact;
      const varKeys = Object.keys(this.whatsappTemplateParams);
      if (varKeys[0] && contact.name)
        this.whatsappTemplateParams[varKeys[0]] = contact.name;
      if (varKeys[1] && contact.email)
        this.whatsappTemplateParams[varKeys[1]] = contact.email;
      if (varKeys[2] && contact.phone_number)
        this.whatsappTemplateParams[varKeys[2]] = contact.phone_number;
    },

    // Atualiza preview quando parâmetros do template mudam
    updateTemplatePreview() {
      if (!this.selectedWhatsappTemplate) return;
      const bodyComponent = this.selectedWhatsappTemplate.components?.find(
        c => c.type === 'BODY'
      );
      let bodyText = bodyComponent?.text || '';

      // Substitui variáveis pelos valores preenchidos
      Object.keys(this.whatsappTemplateParams).forEach(key => {
        const value = this.whatsappTemplateParams[key] || `{{${key}}}`;
        bodyText = bodyText.replace(`{{${key}}}`, value);
      });

      const headerComponent = this.selectedWhatsappTemplate.components?.find(
        c => c.type === 'HEADER'
      );
      const footerComponent = this.selectedWhatsappTemplate.components?.find(
        c => c.type === 'FOOTER'
      );

      let preview = '';
      if (headerComponent?.text)
        preview += `[Header] ${headerComponent.text}\n`;
      preview += bodyText;
      if (footerComponent?.text)
        preview += `\n[Footer] ${footerComponent.text}`;

      this.message = preview;
    },

    getTemplateBodyText(template) {
      const body = template.components?.find(c => c.type === 'BODY');
      return body?.text || '';
    },

    getWaPreviewHeaderType() {
      if (!this.selectedWhatsappTemplate) return 'none';
      const header = this.selectedWhatsappTemplate.components?.find(
        c => c.type === 'HEADER'
      );
      if (!header) return 'none';
      return (header.format || 'TEXT').toLowerCase();
    },

    getWaPreviewHeaderContent() {
      const header = this.selectedWhatsappTemplate?.components?.find(
        c => c.type === 'HEADER'
      );
      return header?.text || header?.example?.header_handle?.[0] || '';
    },

    getWaPreviewFooter() {
      const footer = this.selectedWhatsappTemplate?.components?.find(
        c => c.type === 'FOOTER'
      );
      return footer?.text || '';
    },

    getWaPreviewButtons() {
      const btns = this.selectedWhatsappTemplate?.components?.find(
        c => c.type === 'BUTTONS'
      );
      return btns?.buttons || [];
    },

    getTemplateCategory(template) {
      return template.category || 'N/A';
    },

    getTemplateLanguage(template) {
      return template.language || 'N/A';
    },

    formatTemplateVar(key) {
      return '{{' + key + '}}';
    },

    initializeEnabledFeatures() {
      this.enabledFeatures = this.account.features;
    },

    async fetchScheduledMessages() {
      if (!this.effectiveConversationId) {
        return;
      }
      try {
        const cid = String(this.effectiveConversationId);
        const response = await AccountScheduledMessagesAPI.get();
        const all = response.data?.payload || [];
        this.scheduledMessages = all.filter(
          m => String(m.conversation_display_id) === cid
        );
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.LOAD'), 'error');
      }
    },

    onClose() {
      this.selectedConversationId = null;
      this.$emit('close');
    },

    async fetchConversationsForSelect() {
      try {
        this.loadingConversations = true;
        const all = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const response = await ConversationApi.get({ page });
          const payload = response.data?.data?.payload ?? [];
          all.push(...payload);
          hasMore = payload.length >= 25;
          page += 1;
        }
        this.conversationsForSelect = all;
      } catch (err) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.LOAD'), 'error');
      } finally {
        this.loadingConversations = false;
      }
    },

    openConversationDropdown() {
      this.conversationDropdownOpen = true;
      this.conversationSearch = '';
      this.$nextTick(() => {
        const selector = this.$refs.conversationSelectorRef;
        if (selector) {
          const rect = selector.getBoundingClientRect();
          this.conversationDropdownStyle = {
            position: 'fixed',
            top: `${rect.bottom + window.scrollY}px`,
            left: `${rect.left + window.scrollX}px`,
            width: `${rect.width}px`,
            zIndex: 99999,
          };
        }
        const input = selector?.querySelector('input');
        if (input) input.focus();
      });
    },

    selectConversationForSchedule(conversation) {
      this.selectedConversationId = conversation.display_id || conversation.id;
      this.conversationDropdownOpen = false;
      this.fetchScheduledMessages();
    },

    closeConversationDropdown(e) {
      if (
        this.$refs.conversationSelectorRef &&
        !this.$refs.conversationSelectorRef.contains(e.target)
      ) {
        this.conversationDropdownOpen = false;
      }
    },

    resetForm() {
      this.title = '';
      this.message = '';
      this.selectedInbox = '';
      this.scheduledTime = '';
      this.isLoading = false;
      this.isEditing = false;
      this.editingMessageId = null;
      this.isRecurrent = false;
      this.selectedPeriod = '';
      this.selectedTemplate = null;
      this.selectedWhatsappTemplate = null;
      this.whatsappTemplateParams = {};
      this.attachedFiles = [];
      this.existingFiles = [];
      this.removeFileIds = [];
    },

    onFileChange(event) {
      const selectedFiles = Array.from(event.target.files);
      this.attachedFiles = [...this.attachedFiles, ...selectedFiles];
    },

    removeExistingFile(fileId) {
      this.removeFileIds.push(fileId);
      this.existingFiles = this.existingFiles.filter(f => f.id !== fileId);
    },
    removeAttachedFile(index) {
      this.attachedFiles.splice(index, 1);
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    },

    async onSubmit() {
      if (!this.effectiveConversationId) return;
      try {
        this.isLoading = true;

        const payload = {
          conversation_id: this.effectiveConversationId,
          inbox_id: this.selectedInbox,
          content: this.message,
          scheduled_at: new Date(this.scheduledTime).toISOString(),
          title: this.title,
          is_recurrent: this.isRecurrent,
          period: this.selectedPeriod,
          files: this.attachedFiles,
        };

        // Adiciona dados do template WhatsApp se selecionado
        if (this.selectedWhatsappTemplate) {
          payload.whatsapp_template = {
            name: this.selectedWhatsappTemplate.name,
            namespace: this.selectedWhatsappTemplate.namespace,
            language: this.selectedWhatsappTemplate.language,
            components: this.selectedWhatsappTemplate.components,
            params: this.whatsappTemplateParams,
          };
        }

        await ConversationApi.scheduleMessage(payload);

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.CREATE'), 'success');
        await this.fetchScheduledMessages();
        this.resetForm();
        this.onClose();
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.CREATE'), 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async updateScheduledMessage() {
      try {
        this.isLoading = true;
        const data = {
          inbox_id: this.selectedInbox,
          content: this.message,
          scheduled_at: new Date(this.scheduledTime).toISOString(),
          title: this.title,
          is_recurrent: this.isRecurrent,
          period: this.selectedPeriod,
          files: this.attachedFiles,
          remove_file_ids: this.removeFileIds,
        };

        await ConversationApi.updateScheduledMessage(
          this.effectiveConversationId,
          this.editingMessageId,
          data
        );

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.UPDATE'), 'success');
        await this.fetchScheduledMessages();
        this.resetForm();
        this.onClose();
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.UPDATE'), 'error');
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    getInboxName(inboxId) {
      const inbox = this.inboxes.find(i => i.id === inboxId);
      return inbox
        ? inbox.name
        : this.$t('SCHEDULE_MESSAGES.ERROR.INBOX_NOT_FOUND');
    },

    getContactDisplayName(msg) {
      const name = msg?.contact_name;
      const email = msg?.contact_email;
      const fallback = this.$t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');
      const text = name || email || fallback;
      return text.length > 25 ? `${text.substring(0, 25)}...` : text;
    },

    goToConversation(msg) {
      if (!msg?.conversation_display_id || !this.accountId) return;
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.accountId,
          inboxId: msg.inbox_id,
          conversation_id: msg.conversation_display_id,
        },
      });
    },

    getStatusText(status) {
      return status === 'sent'
        ? this.$t('SCHEDULE_MESSAGES.STATUS.SENT')
        : this.$t('SCHEDULE_MESSAGES.STATUS.PENDING');
    },

    getStatusClass(status) {
      return {
        'status-pending': status === 'pending',
        'status-sent': status === 'sent',
      };
    },

    handleEdit(message) {
      this.title = message.title;
      this.message = message.message;
      this.selectedInbox = message.inbox_id;

      // Converte o timestamp para o formato datetime-local
      const date = new Date(message.scheduled_at * 1000);
      this.scheduledTime = date.toISOString().slice(0, 16);

      // Carrega dados de recorrência
      this.isRecurrent = message.is_recurrent || false;
      if (
        message.period &&
        typeof message.period === 'object' &&
        message.period.type
      ) {
        this.selectedPeriod = message.period.type;
      } else if (typeof message.period === 'string') {
        this.selectedPeriod = message.period;
      } else {
        this.selectedPeriod = '';
      }

      // Limpa template selecionado na edição
      this.selectedTemplate = null;

      // Carrega arquivos existentes da mensagem
      this.existingFiles = message.files || [];
      this.attachedFiles = [];
      this.removeFileIds = [];

      this.isEditing = true;
      this.editingMessageId = message.id;
      this.isScheduledMessagesOpen = false;
    },

    async deleteMessage(id) {
      this.messageToDelete = id;
      this.showDeleteConfirmation = true;
    },

    closeDeleteModal() {
      this.showDeleteConfirmation = false;
      this.messageToDelete = null;
    },

    async confirmDelete() {
      try {
        await ConversationApi.deleteScheduledMessage(
          this.effectiveConversationId,
          this.messageToDelete
        );
        await this.fetchScheduledMessages();

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.DELETE'), 'success');
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.DELETE'), 'error');
      } finally {
        this.closeDeleteModal();
      }
    },

    toggleScheduledMessages() {
      this.isScheduledMessagesOpen = !this.isScheduledMessagesOpen;
    },

    handleSubmit(event) {
      if (!this.isEditing) {
        this.onSubmit();
      }
    },

    toggleMessageExpand(messageId) {
      const index = this.expandedMessages.indexOf(messageId);
      if (index === -1) {
        this.expandedMessages.push(messageId);
      } else {
        this.expandedMessages.splice(index, 1);
      }
    },

    openFilterMenu(event) {
      event.stopPropagation();
      const rect = event.target.getBoundingClientRect();
      this.filterMenuPosition = {
        x: rect.left,
        y: rect.bottom + 4,
      };
      this.showFilterMenu = true;
    },

    closeFilterMenu() {
      this.showFilterMenu = false;
    },

    handleFilterSelect(item) {
      this.statusFilter = item.id;
      this.closeFilterMenu();
    },

    async handleResend(message) {
      try {
        this.isLoading = true;

        // Calcula 5 segundos a partir do horário atual
        const now = new Date();
        const scheduledTime = new Date(now.getTime() + 5000); // 5 segundos

        const data = {
          inbox_id: message.inbox_id,
          content: message.message,
          scheduled_at: scheduledTime.toISOString(),
          title: message.title,
        };

        // Se a mensagem original era recorrente, mantém a recorrência
        if (message.is_recurrent && message.period) {
          data.is_recurrent = message.is_recurrent;
          data.period = message.period;
        }

        // Re-anexa os arquivos da mensagem original
        if (message.files && message.files.length > 0) {
          const fileBlobs = [];
          for (const file of message.files) {
            try {
              const response = await fetch(file.url);
              const blob = await response.blob();
              fileBlobs.push(
                new File([blob], file.filename, { type: file.content_type })
              );
            } catch (e) {
              console.error('Erro ao re-anexar arquivo:', e);
            }
          }
          if (fileBlobs.length > 0) data.files = fileBlobs;
        }

        await ConversationApi.scheduleMessage({
          conversation_id: this.effectiveConversationId,
          ...data,
        });

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.RESEND'), 'success');
        await this.fetchScheduledMessages();
      } catch (error) {
        console.error('Erro ao reenviar mensagem:', error);
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.CREATE'), 'error');
      } finally {
        this.isLoading = false;
      }
    },

    getPeriodText(period) {
      // Se period for um objeto JSON, extrai o tipo
      const periodType =
        typeof period === 'object' && period !== null ? period.type : period;

      switch (periodType) {
        case 'every_day':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY');
        case 'every_day_at_time':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY_AT_TIME');
        case 'every_week':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEK');
        case 'every_month':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_MONTH');
        case 'every_weekday':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKDAY');
        case 'every_weekend':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKEND');
        default:
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY');
      }
    },
  },
};
</script>

<template>
  <div class="schedule-message-container">
    <woot-modal
      :show="isFeatureEnabled && show"
      :on-close="onClose"
      :show-close-button="true"
      :close-on-backdrop-click="true"
      size="large"
    >
      <!-- Header do Modal -->
      <div class="modal-header">
        <div class="header-content">
          <div class="title-group">
            <h1 class="modal-title">{{ $t('SCHEDULE_MESSAGES.TITLE') }}</h1>
          </div>
          <Button
            v-if="selectedConversationId"
            variant="ghost"
            color="slate"
            size="sm"
            class="shrink-0"
            @click="selectedConversationId = null"
          >
            {{ $t('SCHEDULE_MESSAGES.CHANGE_CONVERSATION') }}
          </Button>
        </div>
      </div>

      <!-- Container dos blocos -->
      <div v-if="effectiveConversationId" class="schedule-message">
        <!-- Coluna do Formulário -->
        <div class="form-column">
          <form
            class="schedule-message__form"
            @submit.prevent="handleSubmit"
            @click.stop
          >
            <!-- Canal (sempre visível, primeiro campo) -->
            <div class="form-group">
              <label class="text-n-slate-12">{{
                $t('SCHEDULE_MESSAGES.FORM.INBOX_LABEL')
              }}</label>
              <select
                v-model="selectedInbox"
                class="form-input bg-n-solid-3 text-n-slate-12"
                required
              >
                <option value="">
                  {{ $t('SCHEDULE_MESSAGES.FORM.INBOX_PLACEHOLDER') }}
                </option>
                <option
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </div>

            <!-- Demais campos só depois de selecionar um canal -->
            <template v-if="selectedInbox">
              <!-- Título (sempre nos 2 modos) -->
              <div class="form-group">
                <label class="text-n-slate-12">{{
                  $t('SCHEDULE_MESSAGES.FORM.TITLE_LABEL')
                }}</label>
                <input
                  v-model="title"
                  type="text"
                  class="form-input bg-n-solid-3 text-n-slate-12"
                  :placeholder="$t('SCHEDULE_MESSAGES.FORM.TITLE_PLACEHOLDER')"
                  required
                />
              </div>

              <!-- MODO LIVRE: canal não-oficial (WAHA / API / email / web / etc) -->
              <template v-if="!isSelectedInboxWhatsapp">
                <div class="form-group">
                  <label class="text-n-slate-12">{{
                    $t('SCHEDULE_MESSAGES.FORM.TEMPLATE_LABEL')
                  }}</label>
                  <div class="template-selector">
                    <select
                      v-model="selectedTemplate"
                      class="form-input bg-n-solid-3 text-n-slate-12"
                      @change="applyTemplate"
                    >
                      <option value="">
                        {{ $t('SCHEDULE_MESSAGES.FORM.TEMPLATE_PLACEHOLDER') }}
                      </option>
                      <option
                        v-for="template in templates"
                        :key="template.id"
                        :value="template"
                      >
                        {{
                          template.title ||
                          template.content.substring(0, 50) + '...'
                        }}
                      </option>
                    </select>
                    <div v-if="loadingTemplates" class="template-loading">
                      <span class="loading-spinner" />
                      <span class="loading-text">{{
                        $t('SCHEDULE_MESSAGES.FORM.TEMPLATE_LOADING')
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label class="text-n-slate-12">{{
                    $t('SCHEDULE_MESSAGES.FORM.MESSAGE_LABEL')
                  }}</label>
                  <textarea
                    v-model="message"
                    class="form-input bg-n-solid-3 text-n-slate-12"
                    :placeholder="
                      $t('SCHEDULE_MESSAGES.FORM.MESSAGE_PLACEHOLDER')
                    "
                  />
                </div>
              </template>

              <!-- MODO API OFICIAL: Template WhatsApp -->
              <div v-if="isSelectedInboxWhatsapp" class="form-group">
                <label class="text-n-slate-12">
                  Template WhatsApp (API Oficial)
                </label>
                <div v-if="loadingWhatsappTemplates" class="template-loading">
                  <span class="loading-spinner" />
                  <span class="loading-text">Carregando templates...</span>
                </div>
                <div v-else>
                  <select
                    v-model="selectedWhatsappTemplate"
                    class="form-input bg-n-solid-3 text-n-slate-12"
                    @change="applyWhatsappTemplate"
                  >
                    <option :value="null">
                      Selecione um template WhatsApp
                    </option>
                    <option
                      v-for="tmpl in approvedWhatsappTemplates"
                      :key="tmpl.name"
                      :value="tmpl"
                    >
                      {{ tmpl.name }} ({{ tmpl.language }}) —
                      {{ getTemplateCategory(tmpl) }}
                    </option>
                  </select>
                  <p
                    v-if="approvedWhatsappTemplates.length === 0"
                    class="text-xs text-n-slate-10 mt-1"
                  >
                    Nenhum template aprovado encontrado para este canal.
                  </p>
                </div>

                <!-- Preview do template selecionado -->
                <div v-if="selectedWhatsappTemplate" class="mt-2">
                  <WhatsAppTemplatePreview
                    :header-type="getWaPreviewHeaderType()"
                    :header-content="getWaPreviewHeaderContent()"
                    :body="getTemplateBodyText(selectedWhatsappTemplate)"
                    :footer="getWaPreviewFooter()"
                    :buttons="getWaPreviewButtons()"
                    :variables="whatsappTemplateParams"
                    :dark-mode="isDarkMode"
                  />

                  <!-- Campos de variáveis -->
                  <div
                    v-if="Object.keys(whatsappTemplateParams).length > 0"
                    class="mt-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                  >
                    <p
                      class="text-xs font-medium text-green-600 dark:text-green-400 mb-2"
                    >
                      Preencha as variáveis:
                    </p>
                    <div
                      v-for="(value, key) in whatsappTemplateParams"
                      :key="key"
                      class="flex items-center gap-2 mb-1"
                    >
                      <span
                        class="text-xs font-mono text-green-600 dark:text-green-400 w-12"
                      >
                        {{ formatTemplateVar(key) }}
                      </span>
                      <input
                        v-model="whatsappTemplateParams[key]"
                        type="text"
                        class="flex-1 text-sm px-2 py-1 rounded border border-green-200 dark:border-green-700 bg-n-solid-2 text-n-slate-12"
                        :placeholder="'Valor para variável ' + key"
                        @input="updateTemplatePreview"
                      />
                      <select
                        class="text-xs px-1 py-0.5 rounded border border-green-200 dark:border-green-700 bg-n-solid-2"
                        @change="
                          whatsappTemplateParams[key] = $event.target.value;
                          $event.target.value = '';
                        "
                      >
                        <option value="">Preencher com...</option>
                        <option
                          v-if="currentContact.name"
                          :value="currentContact.name"
                        >
                          Nome: {{ currentContact.name }}
                        </option>
                        <option
                          v-if="currentContact.email"
                          :value="currentContact.email"
                        >
                          Email: {{ currentContact.email }}
                        </option>
                        <option
                          v-if="currentContact.phone_number"
                          :value="currentContact.phone_number"
                        >
                          Tel: {{ currentContact.phone_number }}
                        </option>
                        <option
                          v-for="(
                            val, attrKey
                          ) in currentContact.custom_attributes || {}"
                          :key="attrKey"
                          :value="String(val)"
                        >
                          {{ attrKey }}: {{ val }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="text-n-slate-12">{{
                  $t('SCHEDULE_MESSAGES.FORM.DATETIME_LABEL')
                }}</label>
                <input
                  v-model="scheduledTime"
                  type="datetime-local"
                  class="form-input bg-n-solid-3 text-n-slate-12"
                  required
                />
              </div>

              <!-- MODO LIVRE apenas: Mensagem recorrente + Arquivos -->
              <template v-if="!isSelectedInboxWhatsapp">
                <div class="form-group">
                  <div class="checkbox-group">
                    <label class="checkbox-label">
                      <input
                        v-model="isRecurrent"
                        type="checkbox"
                        class="checkbox-input"
                      />
                      <span class="checkbox-text text-n-slate-12">
                        {{ $t('SCHEDULE_MESSAGES.FORM.RECURRENT_LABEL') }}
                      </span>
                    </label>
                  </div>
                </div>

                <div v-if="isRecurrent" class="form-group">
                  <label class="text-n-slate-12">{{
                    $t('SCHEDULE_MESSAGES.FORM.FREQUENCY_LABEL')
                  }}</label>
                  <select
                    v-model="selectedPeriod"
                    class="form-input bg-n-solid-3 text-n-slate-12"
                    required
                  >
                    <option value="">
                      {{ $t('SCHEDULE_MESSAGES.FORM.FREQUENCY_PLACEHOLDER') }}
                    </option>
                    <option value="every_day">
                      {{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY') }}
                    </option>
                    <option value="every_day_at_time">
                      {{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY_AT_TIME') }}
                    </option>
                    <option value="every_week">
                      {{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEK') }}
                    </option>
                    <option value="every_month">
                      {{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_MONTH') }}
                    </option>
                    <option value="every_weekday">
                      {{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKDAY') }}
                    </option>
                    <option value="every_weekend">
                      {{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKEND') }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="text-n-slate-12">
                    {{ $t('SCHEDULE_MESSAGES.FORM.FILES_LABEL') }}
                  </label>
                  <div class="flex items-center gap-2">
                    <label
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl cursor-pointer bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-2 border border-n-weak"
                    >
                      <span class="i-ri-attachment-line size-3.5" />
                      {{ $t('SCHEDULE_MESSAGES.FORM.FILES_BUTTON') }}
                      <input
                        type="file"
                        multiple
                        class="hidden"
                        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,audio/*,video/*"
                        @change="onFileChange"
                      />
                    </label>
                  </div>
                  <div v-if="existingFiles.length > 0" class="mt-2 space-y-1">
                    <div
                      v-for="file in existingFiles"
                      :key="'existing-' + file.id"
                      class="flex items-center justify-between px-2 py-1 rounded bg-n-alpha-2"
                    >
                      <span class="text-sm truncate text-n-slate-12">
                        📎 {{ file.filename }} ({{
                          formatFileSize(file.byte_size)
                        }})
                      </span>
                      <button
                        type="button"
                        class="text-n-slate-10 hover:text-red-500"
                        @click="removeExistingFile(file.id)"
                      >
                        <span class="i-ri-close-line size-3" />
                      </button>
                    </div>
                  </div>
                  <div v-if="attachedFiles.length > 0" class="mt-2 space-y-1">
                    <div
                      v-for="(file, index) in attachedFiles"
                      :key="'new-' + index"
                      class="flex items-center justify-between px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20"
                    >
                      <span class="text-sm truncate text-n-slate-12">
                        {{ file.name }} ({{ formatFileSize(file.size) }})
                      </span>
                      <button
                        type="button"
                        class="text-n-slate-10 hover:text-red-500"
                        @click="removeAttachedFile(index)"
                      >
                        <span class="i-ri-close-line size-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </template>

            <div class="modal-footer">
              <div class="button-wrapper">
                <Button
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="onClose"
                >
                  {{ $t('SCHEDULE_MESSAGES.CANCEL') }}
                </Button>

                <!-- Botão de Agendar -->
                <Button
                  v-if="!isEditing"
                  variant="solid"
                  color="blue"
                  size="sm"
                  type="submit"
                >
                  {{ $t('SCHEDULE_MESSAGES.BUTTON_LABEL') }}
                </Button>

                <!-- Botão de Salvar alterações -->
                <Button
                  v-else
                  variant="solid"
                  color="teal"
                  size="sm"
                  type="button"
                  @click="updateScheduledMessage"
                >
                  {{ $t('SCHEDULE_MESSAGES.SAVE_CHANGES') }}
                </Button>
              </div>
            </div>
          </form>
        </div>

        <!-- Coluna da Lista de Agendamentos -->
        <div class="scheduled-list-column">
          <div class="scheduled-messages">
            <div class="scheduled-messages-header">
              <div class="header-content">
                <div class="title-group">
                  <h3 class="text-n-slate-12">
                    {{ $t('SCHEDULE_MESSAGES.LIST.TITLE') }}
                    <span v-if="scheduledMessages.length > 0" class="counter">
                      {{ filteredMessages.length }}
                    </span>
                  </h3>

                  <!-- Filtro apenas se houver mensagens -->
                  <WootButton
                    v-if="scheduledMessages.length > 0"
                    variant="clear"
                    size="small"
                    class="filter-button"
                    @click="openFilterMenu($event)"
                  >
                    <span class="i-ri-filter-line size-4" />
                  </WootButton>
                </div>
              </div>
            </div>

            <!-- Estado vazio -->
            <div v-if="scheduledMessages.length === 0" class="empty-state">
              <div class="empty-state-content">
                <svg
                  width="48"
                  height="48"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  class="empty-icon"
                >
                  <path
                    d="M17.75 3A3.25 3.25 0 0 1 21 6.25v11.5A3.25 3.25 0 0 1 17.75 21H6.25A3.25 3.25 0 0 1 3 17.75V6.25A3.25 3.25 0 0 1 6.25 3h11.5Zm1.75 5.5h-15v9.25c0 .966.784 1.75 1.75 1.75h11.5a1.75 1.75 0 0 0 1.75-1.75V8.5Zm-11.75 6a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm-4.25-4a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm1.5-6H6.25A1.75 1.75 0 0 0 4.5 6.25V7h15v-.75a1.75 1.75 0 0 0-1.75-1.75Z"
                    fill="currentColor"
                  />
                </svg>
                <p class="empty-text">{{ $t('SCHEDULE_MESSAGES.EMPTY') }}</p>
                <p class="empty-subtext">
                  {{ $t('SCHEDULE_MESSAGES.EMPTY_SUBTEXT') }}
                </p>
              </div>
            </div>

            <!-- Lista de mensagens (existente) -->
            <div v-else class="scheduled-messages-list">
              <div
                v-for="message in filteredMessages"
                :key="message.id"
                class="scheduled-message"
              >
                <!-- Cabeçalho do Card -->
                <div
                  class="message-header"
                  @click="toggleMessageExpand(message.id)"
                >
                  <div class="header-content">
                    <div class="title-section">
                      <div class="title-and-badge">
                        <span class="title">{{ message.title }}</span>
                        <span
                          class="status-badge"
                          :class="getStatusClass(message.status)"
                        >
                          <span class="pulse-dot" />
                          {{ getStatusText(message.status) }}
                        </span>
                      </div>
                      <fluent-icon
                        :icon="
                          expandedMessages.includes(message.id)
                            ? 'chevron-up'
                            : 'chevron-down'
                        "
                        size="14"
                        class="expand-icon"
                      />
                    </div>

                    <div class="meta-info">
                      <div class="datetime">
                        <span class="i-ri-calendar-line size-3 icon" />
                        {{ formatDate(message.scheduled_at) }}
                      </div>
                      <div class="inbox">
                        <span class="i-ri-chat-1-line size-3 icon" />
                        {{ getInboxName(message.inbox_id) }}
                      </div>
                      <!-- Indicador de Recorrência -->
                      <div v-if="message.is_recurrent" class="recurrence">
                        <span class="i-ri-refresh-line size-3 icon" />
                        <span class="recurrence-text">{{
                          getPeriodText(message.period)
                        }}</span>
                      </div>
                    </div>
                    <div
                      v-if="message.conversation_display_id != null"
                      class="flex items-center gap-2 mt-2 px-3.5 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-xs min-h-[1.5rem] w-full max-w-full cursor-pointer transition-colors hover:bg-n-alpha-2/80"
                      @click.stop="goToConversation(message)"
                    >
                      <div class="flex items-center gap-1 shrink-0">
                        <span class="i-ri-chat-1-line size-3 text-n-slate-10" />
                        <span
                          class="text-[10px] text-n-slate-10 select-all px-0.5"
                        >
                          #{{ message.conversation_display_id }}
                        </span>
                      </div>
                      <div class="flex items-center gap-1 min-w-0 flex-1">
                        <span class="truncate font-semibold text-n-slate-12">
                          {{ getContactDisplayName(message) }}
                        </span>
                        <span class="text-n-slate-9 shrink-0">·</span>
                        <span
                          class="truncate text-n-slate-11 shrink-0 max-w-[120px]"
                        >
                          {{ getInboxName(message.inbox_id) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Conteúdo Expansível -->
                <div
                  v-show="expandedMessages.includes(message.id)"
                  class="message-expandable"
                >
                  <div class="message-preview">
                    <div class="preview-header">
                      <span class="i-ri-chat-1-line size-3.5 preview-icon" />
                      <span>{{
                        $t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL')
                      }}</span>
                      <span
                        v-if="message.whatsapp_template"
                        class="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300"
                      >
                        Template: {{ message.whatsapp_template.name }}
                      </span>
                    </div>
                    <div class="preview-content">
                      {{ message.message }}
                    </div>
                  </div>

                  <div class="actions">
                    <WootButton
                      v-if="message.conversation_display_id"
                      variant="clear"
                      size="small"
                      class="action-button open-chat"
                      @click.stop.prevent="goToConversation(message)"
                    >
                      <span class="i-ri-chat-1-line size-3" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.OPEN_CONVERSATION') }}
                    </WootButton>
                    <WootButton
                      variant="clear"
                      size="small"
                      class="action-button resend"
                      @click.stop.prevent="handleResend(message)"
                    >
                      <span class="i-ri-refresh-line size-3" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.RESEND') }}
                    </WootButton>
                    <WootButton
                      variant="clear"
                      size="small"
                      class="action-button edit"
                      @click.stop.prevent="handleEdit(message)"
                    >
                      <span class="i-ri-pencil-line size-3" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.EDIT') }}
                    </WootButton>
                    <WootButton
                      variant="clear"
                      size="small"
                      class="action-button delete"
                      @click.stop="deleteMessage(message.id)"
                    >
                      <span class="i-ri-delete-bin-line size-3" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.DELETE') }}
                    </WootButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="p-6">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('SCHEDULE_MESSAGES.SELECT_CONVERSATION') }}
        </label>
        <div ref="conversationSelectorRef" class="relative">
          <div
            class="w-full px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-xl cursor-pointer focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            tabindex="0"
            @click="openConversationDropdown"
          >
            <span v-if="selectedConversationForSchedule">
              #{{
                selectedConversationForSchedule.display_id ||
                selectedConversationForSchedule.id
              }}
              -
              {{
                selectedConversationForSchedule.meta?.sender?.name ||
                selectedConversationForSchedule.meta?.sender?.email ||
                $t('KANBAN.ITEM_CONVERSATION.NO_CONTACT')
              }}
            </span>
            <span v-else class="text-n-slate-10">
              {{ $t('KANBAN.FORM.CONVERSATION.PLACEHOLDER') }}
            </span>
          </div>
          <teleport to="body">
            <div
              v-if="conversationDropdownOpen"
              :style="conversationDropdownStyle"
              class="bg-n-solid-2 border border-n-weak rounded-xl shadow-lg max-h-96 overflow-y-auto z-[99999]"
              @mousedown.stop
            >
              <input
                v-model="conversationSearch"
                type="text"
                class="w-full px-3 py-2 mb-2 text-sm border-b border-n-weak bg-transparent focus:outline-none text-n-slate-12"
                :placeholder="$t('KANBAN.SEARCH_CONVERSATION')"
              />
              <div
                v-if="loadingConversations"
                class="px-3 py-4 text-n-slate-11 text-sm"
              >
                {{ $t('KANBAN.LOADING_CONVERSATION') }}
              </div>
              <div v-else class="max-h-72 overflow-y-auto">
                <div
                  v-for="conversation in filteredConversationsForSelect"
                  :key="conversation.id"
                  class="px-3 py-2 hover:bg-woot-50 cursor-pointer text-sm text-n-slate-12"
                  @click="selectConversationForSchedule(conversation)"
                >
                  #{{ conversation.display_id || conversation.id }} -
                  {{
                    conversation.meta?.sender?.name ||
                    conversation.meta?.sender?.email ||
                    $t('KANBAN.ITEM_CONVERSATION.NO_CONTACT')
                  }}
                </div>
                <div
                  v-if="filteredConversationsForSelect.length === 0"
                  class="px-3 py-2 text-n-slate-10 text-sm"
                >
                  {{ $t('KANBAN.FORM.CONVERSATION.NO_CONVERSATIONS') }}
                </div>
              </div>
            </div>
          </teleport>
        </div>
      </div>
    </woot-modal>

    <!-- Modal de Confirmação de Exclusão -->
    <woot-modal
      :show="showDeleteConfirmation"
      :on-close="closeDeleteModal"
      :show-close-button="true"
      :close-on-backdrop-click="true"
    >
      <div class="delete-confirmation-modal">
        <div class="modal-header">
          <div class="header-content">
            <div class="title-group">
              <h1 class="modal-title">
                {{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.TITLE') }}
              </h1>
            </div>
          </div>
        </div>

        <div class="modal-content">
          <p class="modal-message">
            {{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.MESSAGE') }}
          </p>
        </div>

        <div class="modal-footer">
          <div class="button-wrapper">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="closeDeleteModal"
            >
              {{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.KEEP') }}
            </Button>
            <Button
              variant="solid"
              color="ruby"
              size="sm"
              @click="confirmDelete"
            >
              {{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.CONFIRM') }}
            </Button>
          </div>
        </div>
      </div>
    </woot-modal>

    <!-- Adicione isso próximo ao botão de filtro -->
    <ContextMenu
      :show="showFilterMenu"
      :x="filterMenuPosition.x"
      :y="filterMenuPosition.y"
      :items="filterMenuItems"
      :selected-id="statusFilter"
      @select="handleFilterSelect"
      @close="closeFilterMenu"
    />
  </div>
</template>

<style lang="scss" scoped>
.schedule-message-container {
  // Garante largura minima do modal independente do estado interno (v-if).
  // Antes o min-width estava so no .schedule-message (que pode nao renderizar
  // quando effectiveConversationId eh falsy), causando o modal colapsar pra
  // largura do conteudo (so dropdown + botoes = ~400px).
  width: min(90vw, 1200px);
  min-width: 800px;
}

.schedule-message {
  display: flex;
  gap: 24px;
  width: 100%;
  min-width: 800px;
  min-height: 500px;
  max-height: 80vh;

  .form-column {
    width: 45%;
    min-width: 380px;
    padding: 0 24px 24px;
    border-right: 1px solid;
    overflow-y: auto;
    @apply border-n-weak;
  }

  .scheduled-list-column {
    width: 55%;
    min-width: 420px;
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
}

.scheduled-messages {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.scheduled-messages-list {
  flex: 1;
  overflow-y: auto;
  margin-top: 12px;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;

    @apply bg-n-solid-3;
  }

  scrollbar-width: thin;

  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  .dark & {
    scrollbar-color: rgb(71, 85, 105) transparent;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-out;
  max-height: 1000px;
  opacity: 1;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.message-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &.status-pending {
    background-color: #fef3c7;
    color: #92400e;

    .status-dot {
      background-color: #92400e;
    }

    @apply dark:bg-yellow-900 dark:text-yellow-200;

    .status-dot {
      @apply dark:bg-yellow-200;
    }
  }

  &.status-sent {
    background-color: #d1fae5;
    color: #065f46;

    .status-dot {
      background-color: #065f46;
    }

    @apply dark:bg-green-900 dark:text-green-200;

    .status-dot {
      @apply dark:bg-green-200;
    }
  }
}

.scheduled-message {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .message-header {
    padding: 12px 14px;
    cursor: pointer;

    .header-content {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .title-section {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .title-and-badge {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .title {
        font-weight: 500;
        font-size: 13px;
        color: #1a1a1a;
        @apply text-n-slate-12;
      }

      .expand-icon {
        color: #666;
        @apply text-n-slate-11;
      }
    }

    .meta-info {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 12px;
      color: #666;
      @apply text-n-slate-11;

      .datetime,
      .inbox,
      .recurrence {
        display: flex;
        align-items: center;
        gap: 4px;

        .icon {
          color: #1f93ff;
        }
      }

      .recurrence {
        .icon {
          color: #10b981;
        }

        .recurrence-text {
          font-weight: 500;
          color: #10b981;
          @apply dark:text-green-400;
        }
      }
    }
  }

  .message-expandable {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    @apply border-n-weak;
    animation: slideDown 0.2s ease-out;
  }

  .message-preview {
    padding: 12px;
    background-color: rgba(0, 0, 0, 0.02);
    @apply bg-n-solid-3;

    .preview-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 6px;
      font-size: 13px;
      font-weight: 500;
      color: #666;
      @apply text-n-slate-11;

      .preview-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #1f93ff;

        svg {
          width: 14px;
          height: 14px;
        }
      }
    }

    .preview-content {
      padding-left: 20px;
      font-size: 13px;
      line-height: 1.4;
      color: #4a4a4a;
      @apply text-n-slate-12;
      white-space: pre-wrap;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px;
    background-color: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    .action-button {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }

      &.resend {
        color: #34d399;
      }

      &.edit {
        color: #60a5fa;
      }

      &.open-chat {
        color: #64748b;

        @apply text-n-slate-11;
      }

      &.delete {
        color: #ff7875;
      }
    }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &.status-pending {
      background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
      color: #92400e;

      .pulse-dot {
        background-color: currentColor;
      }

      @apply dark:bg-yellow-900/30 dark:text-yellow-300;
    }

    &.status-sent {
      background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
      color: #065f46;

      .pulse-dot {
        background-color: currentColor;
      }

      @apply dark:bg-green-900/30 dark:text-green-300;
    }

    .pulse-dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.4);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(0, 0, 0, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark {
  .scheduled-message {
    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
    border-color: rgba(255, 255, 255, 0.05);

    .actions {
      background-color: #1e293b;
      border-color: rgba(255, 255, 255, 0.1);

      .action-button {
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        &.resend {
          color: #34d399;
        }

        &.edit {
          color: #60a5fa;
        }

        &.open-chat {
          color: #94a3b8;
        }

        &.delete {
          color: #ff7875;
        }
      }
    }

    .status-badge {
      &.status-pending {
        background: linear-gradient(
          135deg,
          rgba(234, 179, 8, 0.2) 0%,
          rgba(234, 179, 8, 0.3) 100%
        );
        color: #fbbf24;
      }

      &.status-sent {
        background: linear-gradient(
          135deg,
          rgba(16, 185, 129, 0.2) 0%,
          rgba(16, 185, 129, 0.3) 100%
        );
        color: #34d399;
      }
    }
  }
}

// Removido o ::v-deep override antigo: nao funcionava porque o woot-modal
// usa <Teleport to="body"> e o seletor scoped nao alcanca elementos teleportados.
// Width agora vem via prop size="large" no woot-modal (1200px max 90%).

.scheduled-messages-header {
  margin-top: 8px;

  .header-content {
    .title-group {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      gap: 8px;

      h3 {
        font-size: 14px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;

        .counter {
          @apply bg-woot-500 text-white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          min-width: 24px;
          text-align: center;
          @apply dark:bg-woot-500 dark:text-white;
        }
      }

      .filter-button {
        padding: 4px;
        border-radius: 4px;
        color: #666;
        @apply text-n-slate-11;

        &:hover {
          @apply bg-n-alpha-2;
        }
      }
    }
  }
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid;
  @apply border-n-weak;

  .header-content {
    display: flex;
    align-items: center;
    height: 32px;

    .title-group {
      .modal-title {
        font-size: 18px;
        font-weight: 600;
        @apply text-n-slate-12;
        white-space: nowrap;
      }
    }
  }
}

.form-group {
  input[type='datetime-local'] {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid;
    @apply border-n-weak;
    border-radius: 4px;
    font-size: 14px;
    @apply bg-white bg-n-solid-3;
    @apply text-n-slate-12;

    &:focus {
      outline: none;
      @apply border-woot-500;
      box-shadow: 0 0 0 2px rgba(31, 147, 255, 0.1);
    }

    &::-webkit-calendar-picker-indicator {
      filter: invert(0.5);
      @apply dark:invert;
      cursor: pointer;
      opacity: 0.6;

      &:hover {
        opacity: 1;
      }
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid;
    @apply border-n-weak;
    border-radius: 4px;
    font-size: 14px;
    @apply bg-white bg-n-solid-3;
    @apply text-n-slate-12;

    &:focus {
      outline: none;
      @apply border-woot-500;
      box-shadow: 0 0 0 2px rgba(31, 147, 255, 0.1);
    }
  }

  .checkbox-group {
    margin-top: 8px;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;

    .checkbox-input {
      width: 16px;
      height: 16px;
      accent-color: #10b981;
      cursor: pointer;
    }

    .checkbox-text {
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.modal-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid;
  @apply border-n-weak;

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    button {
      min-width: 80px;
    }
  }
}

.delete-confirmation-modal {
  padding: 1.5rem;

  .modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    @apply text-n-slate-12;
  }

  .modal-message {
    @apply text-n-slate-12;
  }

  .modal-footer {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid;
    @apply border-n-weak;

    .button-wrapper {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;

  .empty-state-content {
    text-align: center;

    .empty-icon {
      display: block;
      margin: 0 auto 16px auto;
      @apply text-n-blue-10;
    }

    .empty-text {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
      @apply text-n-slate-12;
    }

    .empty-subtext {
      font-size: 13px;
      @apply text-n-slate-11;
    }
  }
}

// Estilos para o template selector
.template-selector {
  position: relative;

  .template-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 12px;
    @apply text-n-slate-11;

    .loading-spinner {
      width: 12px;
      height: 12px;
      border: 2px solid;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      @apply border-n-weak;
    }

    .loading-text {
      font-size: 11px;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
